import { Parser } from '@json2csv/plainjs';

import type { GenericObject } from '@/types/common';

import { flattenObj } from './Common.utils';

export type DataSet = {
  name: string;
  data: GenericObject[];
};

export const exportJson = (
  data: unknown[] | DataSet[],
  fileName: string,
  isMultipleDatasets: boolean
) => {
  let jsonData: unknown;

  if (isMultipleDatasets) {
    // Handle multiple datasets - create structured JSON with dataset names as keys
    const datasets = data as DataSet[];
    jsonData = datasets.reduce(
      (acc, dataset) => {
        acc[dataset.name] = dataset.data;
        return acc;
      },
      {} as Record<string, unknown[]>
    );
  } else {
    // Handle single dataset
    jsonData = data;
  }

  const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
    type: 'application/json'
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = `${fileName}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportCsv = (
  data: GenericObject[] | DataSet[],
  fileName: string,
  isMultipleDatasets: boolean
) => {
  let csvContent = '';

  if (isMultipleDatasets) {
    const datasets = data as DataSet[];
    datasets.forEach((dataset, index) => {
      // Add dataset name as a header with separator line
      csvContent += `\n"${dataset.name}"\n`;
      csvContent += `"${'='.repeat(dataset.name.length)}"\n`;

      if (dataset.data.length > 0) {
        // Flatten the data for this dataset
        const flatData = dataset.data.map((d) => flattenObj(d));
        const parser = new Parser();
        const datasetCsv = parser.parse(flatData);
        csvContent += datasetCsv;
      } else {
        csvContent += 'No data available';
      }

      // Add more spacing between datasets (except for the last one)
      if (index < datasets.length - 1) {
        csvContent += '\n\n\n';
        csvContent += '""\n'; // Empty row
      }
    });
  } else {
    // Handle single dataset
    const singleData = data as GenericObject[];
    const flatData = singleData.map((d) => flattenObj(d));
    const parser = new Parser();
    csvContent = parser.parse(flatData);
  }

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
